import storeManager from '../../store/StoreManager';
import apiService from '../../services/ApiService';
import modeType from '../../constants/ModeConstants';
import shapeTypes from '../../constants/ShapeConstants';
import appParameters from '../../components/utils/AppParameters';
import configuration from '../../constants/Configuration';
import { showSavePreloader, showLostSessionAlert } from '../../actions/MenuActions';
import { updateCurrentSlide } from '../../actions/ProductionActions';
import apiServiceUtils from '../../components/utils/ApiServiceUtils';
import playController from './PlayController';
import saveDataUploader from './SaveDataUploader';
import thumbnailDataUploader from './ThumbnailDataUploader';
import audioToolUtils from '../audioTool/utils/AudioToolUtils';
import thumbnailGenerator from '../utils/ThumbnailGenerator';
import fineUploader from './FineUploader';
import textEditorUtils from '../menus/textEditorTool/utils/TextEditorUtils';

const saveManager = {
    AUTOSAVE_TIME: configuration.AUTOSAVE_TIME * 1000,
    contentChanged: false,
    autosaveCounter: 0,
    hrefLocationForIOS: '',
    refreshExportButton: undefined,
    oldContentItemId: -1,
    showSaveAsAlert: undefined,
    timeForLastSave: -1,

    init() {
        fineUploader.init();
        saveDataUploader.init();
        thumbnailDataUploader.init();
    },

    /********************* Data **********************/
    getProductionData() {
        const state = Object.assign({}, storeManager.getState());
        const production = JSON.parse(JSON.stringify(state.production));
        const audioEditor = JSON.parse(JSON.stringify(state.audioEditor));
        const title  = appParameters.title;
        var data = {};

        if (!production.slides)
            return undefined;

        if (!audioToolUtils.isAudioTool()) {
            data.storySound = production.storySound;
            data.storySoundVolume = production.storySoundVolume;
            data.hiddenAlerts = production.hiddenAlerts;

            if (production.exportProperties)
                data.exportProperties = production.exportProperties;

            if (state.menu.useImageAsBackground)
                data.useImageAsBackground = true;
        }

        data.theme = appParameters.currentTheme;
        data.title = title;
        data.slides = this.checkSlides(production.slides);
        data.currentFont = textEditorUtils.currentFont;
        data.currentFontSize = textEditorUtils.currentFontSize;

        if (playController.isAudioEditorAvailable())
            data.audioEditor = this.checkTimelines(audioEditor);

        var result = JSON.stringify(data);

        return result;
    },
    checkTimelines(audioEditor) {
        var newAudioEditor = {};
        var i, j;
        var timelines = audioEditor.timelines;

        newAudioEditor.zoomValue = audioEditor.zoomValue >= 0 ? audioEditor.zoomValue : 1;
        newAudioEditor.markerPosition = 0;
        newAudioEditor.trackMenu = -1;
        newAudioEditor.timelines = [];

        for (i=0; i<timelines.length; i++) {
            var timeline = Object.assign({}, timelines[i]);
            var audios = timeline.audios;
            var newAudios = [];

            delete timeline.isSelected;

            for (j=0; j<audios.length; j++) {
                var audio = Object.assign({}, audios[j]);

                delete audio.hrefBlob;
                delete audio.showControlPoint;

                if (audio.inPoint)
                    audio.inPoint = Number(audio.inPoint.toFixed(3));

                if (audio.outPoint)
                    audio.outPoint = Number(audio.outPoint.toFixed(3));

                if (audio.left)
                    audio.left = Number(audio.left.toFixed(3));

                if (audio.duration)
                    audio.duration = Number(audio.duration.toFixed(3));

                newAudios.push(audio);
            }

            timeline.audios = newAudios;
            newAudioEditor.timelines.push(timeline);
        }

        return newAudioEditor;
    },
    checkSlides(slides) {
        const newSlides = slides.map(slide => {
            const newSlide = Object.assign({}, slide);
            var shapes = newSlide.content;

            for (var j=0; j<shapes.length; j++) {
                var shape = shapes[j];

                if (shape) {
                    this.removeCacheBreaker(shape);
                    this.removeProperties(shape);
                    this.checkAnimations(shape);

                    if (shape.type == shapeTypes.GROUP)
                        this.removePropertiesFromGroup(shape);
                }
            }

            if (newSlide.sound && newSlide.sound.indexOf('blob') != -1)
                newSlide.sound = '';

            if (newSlide.soundWav && newSlide.soundWav.length > 0)
                newSlide.soundWav = '';

            return newSlide;
        });

        return newSlides;
    },
    removeCacheBreaker(shape) {
        if (shape.xlinkHref && shape.xlinkHref.indexOf('?cacheBreaker') != -1)
            shape.xlinkHref = shape.xlinkHref.substring(0, shape.xlinkHref.indexOf('?cacheBreaker'));
    },
    removePropertiesFromGroup(group) {
        if (!group.shapes)
            return;

        for (var i=0; i<group.shapes.length; i++) {
            var shape = group.shapes[i];

            this.removeCacheBreaker(shape);

            if (shape.type == shapeTypes.GROUP)
                this.removePropertiesFromGroup(shape);

            this.removeProperties(shape);
        }
    },
    removeProperties(shape) {
        if (!shape)
            return;

        delete shape.isSelected;
        delete shape.isInteraction;
        delete shape.moveState;
        delete shape.isNew;
        delete shape.path;

        if (shape.filter == '')
            delete shape.filter;

        if (shape.hotSpot == '')
            delete shape.hotSpot;

        if (shape.logicalName == '')
            delete shape.logicalName;

        if (shape.frameLabel == '')
            delete shape.frameLabel;

        if (shape.xlinkHref == '')
            delete shape.xlinkHref;

        if (shape.isLocked == false)
            delete shape.isLocked;

        if (shape.x)
            shape.x = Number(shape.x.toFixed(2));

        if (shape.y)
            shape.y = Number(shape.y.toFixed(2));

        if (shape.width)
            shape.width = Number(shape.width.toFixed(2));

        if (shape.height)
            shape.height = Number(shape.height.toFixed(2));

        if (shape.cx)
            shape.cx = Number(shape.cx.toFixed(2));

        if (shape.cy)
            shape.cy = Number(shape.cy.toFixed(2));
    },
    checkAnimations(shape) {
        var animation = shape.animation;

        if (animation) { // animations should always have the initial position
            if (animation.start_x != shape.x)
                shape.x = animation.start_x;

            if (animation.start_y != shape.y)
                shape.y = animation.start_y;

            if (animation.start_width != shape.width)
                shape.width = animation.start_width;

            if (animation.start_height != shape.height)
                shape.height = animation.start_height;

            if (animation.start_opacity != shape.opacity)
                shape.opacity = animation.start_opacity;

            if (animation.start_rotation != shape.rotation)
                shape.rotation = animation.start_rotation;

            if (animation.start_x1 != shape.x1)
                shape.x1 = animation.start_x1;

            if (animation.start_y1 != shape.y1)
                shape.y1 = animation.start_y1;

            if (animation.start_x2 != shape.x2)
                shape.x2 = animation.start_x2;

            if (animation.start_y2 != shape.y2)
                shape.y2 = animation.start_y2;

            if (animation.start_cx != shape.cx)
                shape.cx = animation.start_cx;

            if (animation.start_cy != shape.cy)
                shape.cy = animation.start_cy;

            if (animation.start_cx1 != shape.cx1)
                shape.cx1 = animation.start_cx1;

            if (animation.start_cy1 != shape.cy1)
                shape.cy1 = animation.start_cy1;

            if (animation.start_cx2 != shape.cx2)
                shape.cx2 = animation.start_cx2;

            if (animation.start_cy2 != shape.cy2)
                shape.cy2 = animation.start_cy2;

            if (animation.start_cx3 != shape.cx3)
                shape.cx3 = animation.start_cx3;

            if (animation.start_cy3 != shape.cy3)
                shape.cy3 = animation.start_cy3;

            if (animation.start_cx4 != shape.cx4)
                shape.cx4 = animation.start_cx4;

            if (animation.start_cy4 != shape.cy4)
                shape.cy4 = animation.start_cy4;

            if (animation.start_cx5 != shape.cx5)
                shape.cx5 = animation.start_cx5;

            if (animation.start_cy5 != shape.cy5)
                shape.cy5 = animation.start_cy5;
        }
    },
    /***********************************************************/

    getMode() {
        const state = storeManager.getState();
        const mode = state.production.mode;

        return mode;
    },
    dispatch() {
        const store = storeManager.getStore();

        return store.dispatch;
    },
    saveContent(autosave = false) {
        if (configuration.LOAD_LOCAL == true)
            return;

        const currentTime = new Date().getTime();

        if (autosave === true && this.timeForLastSave > 0 && (currentTime - this.timeForLastSave) < 6 * 1000) // if by mistake an autosave occurs in max 6 seconds then don't do it
            return;

        if (this.timeForLastSave > 0)
            console.log('saveTime ' + (currentTime - this.timeForLastSave));

        const dispatch = this.dispatch();
        dispatch(showSavePreloader(true));

        apiServiceUtils.saveTime = currentTime;
        this.timeForLastSave = currentTime;

        if (autosave == false)
            this.storeData(autosave);
        else {
            dispatch(updateCurrentSlide());
            setTimeout(this.storeDataWhenProductionDataIsReady.bind(this), 10);
        }
    },
    storeDataWhenProductionDataIsReady() {
        this.storeData(true);
    },
    storeData(autosave) {
        apiServiceUtils.executeSafeApiCall(function() {
            return this.executeStoreData(autosave);
        }.bind(this), function() {
            this.showExceptionAlert(1);
        }.bind(this));
    },
    overrideProduction() {
        if (appParameters.title == 'aaa111')
            return 58530960; // hree Billy Goats;

        if (appParameters.title == 'aaa222')
            return 58517861; // Stone ages

        if (appParameters.title == 'aaa333')
            return 62344225; // Master Norway

        return -1;
    },
    getContentItemId() {
        var contentItemId = -1;

        if (appParameters.submitId > 0) { // is a saved production
            contentItemId = appParameters.contentItemId;

            if (!appParameters.isAssignment) { // if is not an assigned product
                if (appParameters.currentUserId > -1 && appParameters.creatorUserId > -1 && appParameters.currentUserId != appParameters.creatorUserId)
                    contentItemId = -1;
            }
        }

        if (appParameters.assignmentStatus && appParameters.assignmentStatus == 'INVITED') // for the assigned productions which are not yet started
            contentItemId = -1;

        // it's when click on Create button from Exercises or Tools
        if (appParameters.isExercise)
            contentItemId = -1;

        // there are new exercises (one of them can be bases on productions with content) so the new contentItemId should be -1 to not override the current one
        if (appParameters.submitId < 0)
            contentItemId = -1;

        if (appParameters.isMasterProduction == true || appParameters.isMaster) // is a master production so it shouldn't be overriten
            contentItemId = -1;

        var overrideProduction = this.overrideProduction();

        if (overrideProduction > 0)
            return overrideProduction;

        return contentItemId;
    },
    executeStoreData(autosave) {
        var data = this.getProductionData();
        var contentItemId = this.getContentItemId();
        var submitId = appParameters.submitId;
        var isAudio = audioToolUtils.isAudioTool() ? true : false;
        var exerciseId = -1;

        if (appParameters.exerciseId > 0)
            exerciseId = appParameters.exerciseId;

        if (autosave == true)
            this.autosaveCounter = this.autosaveCounter + 1;
        else
            this.autosaveCounter = 0;

        var autoSaveFlag = autosave;

        if (this.autosaveCounter >= 20) { // at every 20 autosaves we should do a proper save with revision and thumbnail
            this.autosaveCounter = 0;
            autoSaveFlag = false;
        }

        if (data == undefined || data == '' || data == ' ' || data == null) {
            this.showExceptionAlert(2);
            return;
        }

        var clipartReferenceId = appParameters.clipartId;

        if (Number.isInteger(clipartReferenceId) && clipartReferenceId > 0) { // it's ok
            // the clipart it's an integer > 0
        } else {
            if (configuration.IS_PROD() == true)
                clipartReferenceId = configuration.DEFAULT_CLIPART_ID_PROD;
            else
                clipartReferenceId = configuration.DEFAULT_CLIPART_ID;
        }

        if (clipartReferenceId == 111) {
            if (configuration.IS_PROD() == true)
                clipartReferenceId = configuration.DEFAULT_CLIPART_ID_PROD;
            else
                clipartReferenceId = configuration.DEFAULT_CLIPART_ID;
        }

        var thumbnailName;
        var thumbnailS3Key;

        if (audioToolUtils.isAudioTool()) {
            if (configuration.IS_PROD() == true)
                clipartReferenceId = configuration.AUDIO_TOOL_CLIRPART_ID_PROD;
            else
                clipartReferenceId = configuration.AUDIO_TOOL_CLIRPART_ID_STAGE;

            var cloudfrontIndex = appParameters.exerciseThumbnailUrl ? appParameters.exerciseThumbnailUrl.indexOf('cloudfront.net/') : -1;
            if (contentItemId == -1 && cloudfrontIndex > 0) {
                thumbnailS3Key = appParameters.exerciseThumbnailUrl.substring(cloudfrontIndex + 15);
            }
        } else { // store thumbnail only for Cartoonist
            if (!autoSaveFlag || (autoSaveFlag && contentItemId == -1)) { // it's a normal save so a new revision is created ... or it's the first autosave (no contentItemId)
                thumbnailName = this.getThumbnailName();
                thumbnailS3Key = 'Cartoonist/' + appParameters.currentUserId + '/' + thumbnailName;
            }
        }

        this.oldContentItemId = contentItemId;

        if (!configuration.IS_PROD())
            clipartReferenceId = 70810402;
        
        if (this.isBigData(data)) {
            var properties = {};

            properties.title = appParameters.title;
            properties.contentItemId = contentItemId;
            properties.submitId = submitId;
            properties.exerciseId = exerciseId;
            properties.clipartReferenceId = clipartReferenceId;
            properties.autoSave = autoSaveFlag;
            properties.thumbnailS3Key = thumbnailS3Key;

            this.executeNewSave(data, properties);
        } else {
            apiService.storeCartoonist(appParameters.title, contentItemId, submitId, exerciseId, data, clipartReferenceId, thumbnailS3Key, autoSaveFlag, isAudio)
                .then(response => {
                    return this.dataSaved(response);
                })
                .catch((ex) => {
                    this.showExceptionAlert(3);
                });
        }

        if (thumbnailS3Key && !audioToolUtils.isAudioTool())
            thumbnailGenerator.getProductionThumb(this.storeThumbnail.bind(this), thumbnailName);
    },
    resetAutosave() {
        this.isContentChanged(false);
    },
    saveData() {
        this.saveContent();
    },
    saveAsData() {
        appParameters.contentItemId = -1;
        appParameters.submitId = -1;
        this.saveContent();
    },
    showExceptionAlert(type) {
        const dispatch = this.dispatch();
        dispatch(showSavePreloader(false));

        // when a storing fails then stop autosave functionality
        this.resetAutosave();

        switch(type) {
            case 1: { // Lost sesssion ... default message
                dispatch(showLostSessionAlert(1));
                break;
            }

            case 2: { // Bad Json data
                dispatch(showLostSessionAlert(2));
                break;
            }

            case 3: { // Exception during storing flow
                dispatch(showLostSessionAlert(3));
                break;
            }

            case 4: { // Exception on storing success response
                dispatch(showLostSessionAlert(4));
                break;
            }
        }
    },
    dataSaved(response) {
        if (response.contentItemId > 0) {
            this.resetAutosave();

            setTimeout(function() {
                const dispatch = this.dispatch();
                dispatch(showSavePreloader(false));
            }.bind(this), 1000);

            if (this.showSaveAsAlert)
                this.showSaveAsAlert();

            this.showSaveAsAlert = undefined;

            if (response.contentItemId)
                appParameters.contentItemId = response.contentItemId;

            if (response.submitId) {
                if (appParameters.submitId == -1 && response.stringId) {
                    appParameters.stringId = response.stringId;
                    this.refreshAppWithNewLink();
                }

                appParameters.submitId = response.submitId;
                localStorage.setItem("lockedItemId", response.submitId);
            }

            appParameters.assignmentStatus = 'CREATED';
            appParameters.isMaster = false;
            appParameters.isExercise = false;
            appParameters.isMasterProduction = false;
            appParameters.creatorUserId = appParameters.currentUserId;

            if (appParameters.masterAssignmentId != -1) {
                var masterAssignments = localStorage.getItem("masterAssignments");

                if (masterAssignments && masterAssignments.indexOf(appParameters.masterAssignmentId) != -1) {
                    masterAssignments = masterAssignments.replace(''+appParameters.masterAssignmentId+',', '');
                    localStorage.setItem("masterAssignments", masterAssignments);
                    appParameters.masterAssignmentId = -1;
                }
            }

            if (this.refreshExportButton)
                this.refreshExportButton();
        } else {
            this.showExceptionAlert(4);
        }
    },
    refreshAppWithNewLink() {
        apiService.getSessionProperties() // check if the user has session
            .then(response => {
                var url = window.location.origin + '/' + response.user.userHomePageURL + '/' + appParameters.stringId + '/edit';

                setTimeout(function() {
                    window.parent.history.pushState('', '', url);
                    // window.parent.location.href = url;
                }.bind(this), 1000);
            });
    },
    initAutoSave() {
        this.resetAutosave();

        if (this.getMode() == modeType.EDITOR || audioToolUtils.isAudioTool())
            setInterval(this.autoSave.bind(this), this.AUTOSAVE_TIME);
    },
    autoSave() {
        if (configuration.LOAD_LOCAL == true)
            return;

        if (configuration.AUTOSAVE_AVAILABLE == false || this.contentChanged == false)
            return;

        const mode = this.getMode;
        if (mode == modeType.PREVIEW || mode == modeType.PLAYER || mode == modeType.AUDIO_TOOL_PLAYER)
            return;

        // should call here a new API for updating current revision and not create a new one
        this.saveContent(true);
    },
    isContentChanged(value) {
        this.contentChanged = value;

        var newVal = String(value);

        if (!newVal || newVal == '')
            newVal = 'false';

        localStorage.setItem('cartoonistNeedsSave', newVal);
    },

    /*********** Saving thumbnail +*****************/
    getThumbnailName() {
        var currentdate = new Date();
        var minutes = currentdate.getMinutes();

        if (minutes < 10)
            minutes = '0' + minutes;

        var seconds = currentdate.getSeconds();

        if (seconds < 10)
            seconds = '0' + seconds;

        var title = '' + currentdate.getFullYear() + '-' + (currentdate.getMonth() + 1) + '-' + currentdate.getDate() + '_' + currentdate.getHours() + '_' + minutes + '_' + seconds + '_' + currentdate.getMilliseconds() + '.png';

        return title;
    },
    storeThumbnail(canvasObject, title) {
        if (!canvasObject)
            return;

        try {
            if (!HTMLCanvasElement.prototype.toBlob) {
                Object.defineProperty(HTMLCanvasElement.prototype, 'toBlob', {
                    value: function (callback, type, quality) {
                        var canvas = this;
                        setTimeout(function() {
                            var binStr = atob( canvas.toDataURL(type, quality).split(',')[1] ),
                                len = binStr.length,
                                arr = new Uint8Array(len);

                            for (var i = 0; i < len; i++ ) {
                                arr[i] = binStr.charCodeAt(i);
                            }

                            callback( new Blob( [arr], {type: type || 'image/png'} ) );
                        });
                    }
                });
            }
        } catch (e) {
            // possible error
        }

        try {
            canvasObject.toBlob(function (blob) {
                thumbnailDataUploader.uploadFile(blob, title);
            }.bind(this));
        } catch (e) {
            // possible error
        }
    },
    uploadThumbnailSuccess(type, name, state) {
        if (state && state.success == true) {
        } else { // error: the file wasn't stored on s3
        }
    },
    uploadThumbnailError() {
    },

    /*********** Saving big data +*****************/
    productionProperties: undefined,

    isBigData(data) {
        if (data && data.length > 900000)
            return true;
        else
            return false;
    },
    getFileName() {
        var currentdate = new Date();
        var minutes = currentdate.getMinutes();

        if (minutes < 10)
            minutes = '0' + minutes;

        var seconds = currentdate.getSeconds();

        if (seconds < 10)
            seconds = '0' + seconds;

        var title = '' + currentdate.getFullYear() + '-' + (currentdate.getMonth() + 1) + '-' + currentdate.getDate() + '_' + currentdate.getHours() + '_' + minutes + '_' + seconds + '.json';

        return title;
    },
    getCurrentRevision(key) {
        var prefix = 'Cartoonist/' + appParameters.currentUserId + '/';

        if (key && key.indexOf(prefix) != -1) {
            key = key.replace(prefix, '');

            return key;
        } else
            return undefined;
    },
    executeNewSave(data, properties) {
        var title = this.getFileName();
        this.productionProperties = properties;
        this.productionProperties.s3Key = 'Cartoonist/' + appParameters.currentUserId + '/' + title;
        this.productionProperties.usedContentIds = this.getUsedContentIds();

        if (properties.autoSave) { // it's autosave so should override current revision
            var currentRevision = this.getCurrentRevision(appParameters.s3key);

            if (currentRevision) {
                title = currentRevision;
                this.productionProperties.s3Key = 'Cartoonist/' + appParameters.currentUserId + '/' + title;
            } else {
                this.productionProperties.autoSave = false;
            }
        } else { // it's normal save so create a new revision

        }

        appParameters.s3key = this.productionProperties.s3Key; // set the current revision for autoSave

        var blob = new Blob([data], {type: 'application/json'});

        saveDataUploader.uploadFile(blob, title);
    },
    uploadSuccess(type, name, state) {
        if (state && state.success == true) {
            this.doNewStoring();
        } else { // error: the file wasn't stored on s3
            this.showExceptionAlert(3);
        }
    },
    uploadError() {
        this.showExceptionAlert(3);
    },
    doNewStoring() {
        var properties = this.productionProperties;

        apiService.storeBigDataCartoonist(properties.s3Key, properties.title, properties.contentItemId, properties.submitId, properties.exerciseId, properties.clipartReferenceId, properties.thumbnailS3Key, properties.autoSave, properties.usedContentIds)
            .then(response => {
                return this.dataSaved(response);
            })
            .catch((ex) => {
                this.showExceptionAlert(3);
            });
    },
    getUsedContentIds() {
        var usedContentItemIds = [];
        const state = storeManager.getState();
        var i, j, shape;

        // audio editor
        if (state.audioEditor && state.audioEditor.timelines) {
            var timelines = state.audioEditor.timelines;

            for (i=0; i<timelines.length; i++) {
                var audios = timelines[i].audios;

                for (j=0; j<audios.length; j++) {
                    if (audios[j].contentItemId > 0)
                        this.updateIds(usedContentItemIds, audios[j].contentItemId);
                }
            }
        }

        // story sound
        if (state.storySound && state.storySound.contentItemId > 0) {
            this.updateIds(usedContentItemIds, state.storySound.contentItemId);
        }

        // current slide
        if (state.currentSlide && state.currentSlide.model) {
            if (state.currentSlide.model.contentItemId > 0)
                this.updateIds(usedContentItemIds, state.currentSlide.model.contentItemId);

            if (state.currentSlide.model.contentItemIdSoundEffect > 0)
                this.updateIds(usedContentItemIds, state.currentSlide.model.contentItemIdSoundEffect);

            var shapes = state.currentSlide.model.content;

            for (i=0; i<shapes.length; i++) {
                shape = shapes[i];

                if (shape.contentItemId > 0)
                    this.updateIds(usedContentItemIds, shape.contentItemId);
            }
        }

        // slides
        for (i=0; i<state.slides; i++) {
            var slide = state.slides[i];

            if (slide.contentItemId > 0)
                this.updateIds(usedContentItemIds, slide.contentItemId);

            if (slide.contentItemIdSoundEffect > 0)
                this.updateIds(usedContentItemIds, slide.contentItemIdSoundEffect);

            for (j=0; j<slide.content.length; j++) {
                shape = slide.content[j];

                if (shape.contentItemId > 0)
                    this.updateIds(usedContentItemIds, shape.contentItemId);
            }
        }

        return usedContentItemIds;
    },
    updateIds(array, item) {
        if (array.indexOf(item) == -1)
            array.push(item);
    }
};

export default saveManager;



/** WEBPACK FOOTER **
 ** ./src/components/utils/SaveManager.js
 **/